import { getInitials } from "@common/controls/Avatar/Gravatar/lib";
import { HtmlTag } from "@common/controls/Html";
import Icon from "@common/controls/Icon/component";
import Preloader from "@common/controls/Preloader/component";
import UploadDndFile, { type UploadDndFileChildrenArgs } from "@common/controls/Upload/DndFile";
import { IconType, type Media, Users } from "@common/declarations";
import { updateQueryString } from "@common/lib/url";
import { getUserDisplayName } from "@common/modules/user/lib";
import classNames from "classnames";
import React from "react";
import { MediaImageComponent } from "../Media/component";

import "./style.less";

type ProfileUser = Pick<
  Users.User,
  "firstName" | "lastName" | "profilePictureMediaId" | "profilePictureMediaSourceId" | "profilePictureMediaSourceKey"
> & {
  email: string | null;
};
type Props = {
  isHost?: boolean;
  onChange?: (media: Media.Item | null) => void;
  noImage?: "icon" | "initials";
  nameTag?: string;
  size?: number | string;
  uploadUrl?: string;
  user: ProfileUser;
};

const getMedia = (user: ProfileUser): Media.Item | null =>
  user.profilePictureMediaSourceKey && user.profilePictureMediaSourceId
    ? {
        mediaId: "",
        mediaSourceKey: user.profilePictureMediaSourceKey!,
        sourceId: user.profilePictureMediaSourceId!,
        mediaStatusKey: "Ready",
        mediaTypeKey: "Image",
        name: ""
      }
    : null;

const UserProfilePictureBase = ({ nameTag = "span", noImage = "icon", size = 30, ...props }: Props) => {
  const [media, setMedia] = React.useState(getMedia(props.user));
  React.useEffect(
    () => setMedia(getMedia(props.user)),
    [props.user.profilePictureMediaId, props.user.profilePictureMediaSourceKey, props.user.profilePictureMediaSourceId]
  );

  const onChange = React.useCallback(
    (value: Media.Item) => {
      setMedia(value);
      props.onChange!(value);
    },
    [props.onChange]
  );

  const onClick = React.useCallback(() => {
    media && props.onChange!(null);
    setMedia(null);
  }, [props.onChange, media, setMedia]);

  return (
    <div className="user_profile_picture" style={{ "--size": typeof size === "number" ? `${size}px` : size } as any}>
      <div
        className={classNames("picture", {
          can_upload: !!props.uploadUrl,
          no_image: !media
        })}
        onClick={props.onChange ? onClick : undefined}
      >
        {media ? (
          <>
            <MediaImageComponent aspectRatio={1} sourceKey={media.mediaSourceKey} sourceId={media.sourceId} />
            <Icon className="delete" icon={IconType.Delete} />
          </>
        ) : (
          <>
            {!!props.uploadUrl && (
              <UploadDndFile
                onUploaded={onChange}
                type="Image"
                url={updateQueryString(props.uploadUrl, {
                  location: "UserProfileImages"
                })}
              >
                {(args: UploadDndFileChildrenArgs) => (
                  <span className={classNames("uploader", { busy: args.isUploading })} {...args.rootProps}>
                    {args.isUploading ? (
                      <Preloader isFontSize type="spinner" />
                    ) : (
                      <>
                        <Icon icon={IconType.Add} />
                        <input {...args.inputProps} />
                      </>
                    )}
                  </span>
                )}
              </UploadDndFile>
            )}

            {noImage === "icon" || !getInitials(props.user) ? (
              <Icon className="user" icon={IconType.User} />
            ) : (
              <h6 className="initials user">{getInitials(props.user)}</h6>
            )}
          </>
        )}
      </div>

      {!!getUserDisplayName(props.user) && <HtmlTag tag={nameTag}>{getUserDisplayName(props.user)}</HtmlTag>}
    </div>
  );
};

type AuthorProfileUser = {
  authorEmail?: string | null;
  authorFirstName: string | null;
  authorLastName: string | null;
  authorProfilePictureMediaSourceKey: Media.Source | null;
  authorProfilePictureMediaSourceId?: string | null;
  authorProfilePictureSourceId?: string | null; // wrong name in SQL
};
type ContactProfileUser = {
  contactEmail?: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactProfilePictureMediaSourceKey: Media.Source | null;
  contactProfilePictureMediaSourceId?: string | null;
  contactProfilePictureSourceId?: string | null; // wrong name in SQL
};
type UserProfileUser = {
  userEmail?: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  userProfilePictureMediaSourceKey: Media.Source | null;
  userProfilePictureMediaSourceId?: string | null;
  userProfilePictureSourceId?: string | null; // wrong name in SQL
};
type FallbackProfileUser = Omit<ProfileUser, "email" | "profilePictureMediaId" | "profilePictureMediaSourceId"> & {
  email?: string | null;
  profilePictureMediaSourceId?: string | null;
  profilePictureSourceId?: string | null; // wrong name in SQL
};
export const pickUserProfilePictureProps = (
  x: AuthorProfileUser | ContactProfileUser | UserProfileUser | FallbackProfileUser
): ProfileUser => {
  if ("authorFirstName" in x) {
    return {
      email: x.authorEmail ?? null,
      firstName: x.authorFirstName ?? null,
      lastName: x.authorLastName ?? null,
      profilePictureMediaId: null,
      profilePictureMediaSourceId: x.authorProfilePictureSourceId ?? x.authorProfilePictureMediaSourceId ?? null,
      profilePictureMediaSourceKey: x.authorProfilePictureMediaSourceKey ?? null
    };
  }

  if ("firstName" in x)
    return {
      ...x,
      email: x.email ?? null,
      profilePictureMediaId: null,
      profilePictureMediaSourceId: x.profilePictureSourceId ?? x.profilePictureMediaSourceId ?? null
    };

  if ("contactFirstName" in x) {
    return {
      email: x.contactEmail ?? null,
      firstName: x.contactFirstName ?? null,
      lastName: x.contactLastName ?? null,
      profilePictureMediaId: null,
      profilePictureMediaSourceId: x.contactProfilePictureSourceId ?? x.contactProfilePictureMediaSourceId ?? null,
      profilePictureMediaSourceKey: x.contactProfilePictureMediaSourceKey ?? null
    };
  }

  return {
    email: x.userEmail ?? null,
    firstName: x.userFirstName ?? null,
    lastName: x.userLastName ?? null,
    profilePictureMediaId: null,
    profilePictureMediaSourceId: x.userProfilePictureSourceId ?? x.userProfilePictureMediaSourceId ?? null,
    profilePictureMediaSourceKey: x.userProfilePictureMediaSourceKey ?? null
  };
};

type UserProfilePictureProps = Omit<Props, "onChange" | "noImage" | "uploadUrl" | "nameTag" | "user"> & {
  email: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  profilePictureMediaSourceId: string | null | undefined;
  profilePictureMediaSourceKey: Media.Source | null | undefined;
};
export const UserProfilePicture = ({
  email,
  firstName,
  lastName,
  profilePictureMediaSourceId,
  profilePictureMediaSourceKey,
  ...props
}: UserProfilePictureProps) => {
  const user = React.useMemo(
    (): ProfileUser => ({
      email: email ?? null,
      firstName: firstName ?? null,
      lastName: lastName ?? null,
      profilePictureMediaId: null,
      profilePictureMediaSourceId: profilePictureMediaSourceId ?? null,
      profilePictureMediaSourceKey: profilePictureMediaSourceKey ?? null
    }),
    [email, firstName, lastName, profilePictureMediaSourceId, profilePictureMediaSourceKey]
  );
  return <UserProfilePictureBase {...props} noImage="initials" user={user} />;
};

type UserProfilePictureWithUploadProps = Omit<Props, "onChange" | "uploadUrl"> & {
  onChange: (media: Media.Item | null) => void;
  uploadUrl: string;
};
export const UserProfilePictureWithUpload = (props: UserProfilePictureWithUploadProps) => (
  <UserProfilePictureBase {...props} />
);
